<script setup lang="ts">
import { computed, ref, watch, watchEffect } from "vue";

import { debounce } from "lodash";
import InputMask from "primevue/inputmask";

import { formatCurrency } from "@/helpers/moneys";
import { isset } from "@/helpers/object";

import type { TextInputProps } from "@/dsComponents/inputs/types";

import Loader from "@/dsComponents/Loader.vue";

import { validate } from "./helpers";

const props = withDefaults(defineProps<TextInputProps>(), {
    size: "md",
    type: "text",
    debounce: 0,
});

const emit = defineEmits<{
    "update:modelValue": [value: string | number];
    blur: [event: FocusEvent];
    focus: [];
    "left-icon-click": [];
    "right-icon-click": [];
}>();

const textInput = ref<HTMLInputElement>();
const focus = ref(false);
const localError = ref<string | undefined>("");
const localValid = ref<boolean | undefined>(false);
const localValue = ref<string | number | undefined>(props.modelValue);

const isFilled = computed(() => {
    return localValue.value !== "" && localValue.value !== null && localValue.value !== undefined && localValue.value !== 0;
});

watchEffect(() => {
    localError.value = props.error;
    localValid.value = props.validate;
});

watch(
    () => props.modelValue,
    (newVal) => {
        localValue.value = newVal;
    }
);

watch(
    () => props.focused,
    () => {
        if (textInput.value && props.focused) {
            textInput.value.focus();
        }
    }
);

const emitValue = debounce((inputValue: string) => {
    switch (props.type) {
        case "number":
            emit("update:modelValue", Number(inputValue));
            break;
        case "currency": {
            const numericValue = inputValue.replace(/[^\d.,]/g, "").replace(",", ".");
            emit("update:modelValue", Number(numericValue));
            break;
        }
        default:
            emit("update:modelValue", inputValue);
            break;
    }

    if (localError.value && inputValue && props.validate) {
        const { error, isValid } = validate(inputValue, props.type, props.required);
        localError.value = error;
        localValid.value = isValid;
    }
}, props.debounce);

const handleChange = (event: Event) => {
    const inputValue = (event.target as HTMLInputElement)?.value ?? "";
    localValue.value = inputValue;
    emitValue(inputValue);
};

const handleBlur = (e: FocusEvent) => {
    focus.value = false;
    localValid.value = false;
    emitValue.flush();
    emit("blur", e);
    if (props.validate) {
        const { error, isValid } = validate(localValue.value ?? "", props.type, props.required);
        localError.value = error;
        localValid.value = isValid;
    }
};

const handleFocus = () => {
    focus.value = true;
    emit("focus");
};

const handlePhoneChange = (value: string) => {
    emit("update:modelValue", value);
};

const writingLocker = (event: KeyboardEvent) => {
    const target = event.target as HTMLInputElement;
    const hasSelection = !!event.view?.getSelection()?.toString();
    switch (props.type) {
        case "nir":
            if (target.value.length < 18 && /^\d$/.test(event.key)) return;
            if (target.value.length === 18 && hasSelection) return;
            event.preventDefault();
            break;
        case "evoliaId":
            if (target.value.length < 7 && /^\d$/.test(event.key)) return;
            if (target.value.length === 7 && hasSelection) return;
            event.preventDefault();
            break;
        case "currency": {
            const value = (target.value || "").replace(/\s/g, "");
            const decimalMatch = value.match(/[.,](\d*)$/);
            const hasMaxDecimals = decimalMatch && decimalMatch[1].length >= 2;

            if (/^[.,]$/.test(event.key) && !value.includes(".") && !value.includes(",")) return;
            if (/^\d$/.test(event.key) && !hasMaxDecimals) return;
            event.preventDefault();
            break;
        }
    }
};
</script>
<template>
    <div class="ds-text-input-component" :class="[size, { disabled }]" :style="`width: ${width}`">
        <div v-if="label || description" class="label-description-container">
            <label v-if="label" :class="{ enabled: enableLabel }" :for="name">
                {{ label }}
                <span v-if="required" class="required-asterisk">*</span>
            </label>
            <div class="description" v-if="description">{{ description }}</div>
        </div>
        <div class="input-container" :class="[size, { error: isset(localError) || isError, focus, disabled }]">
            <i v-if="iconLeft" :class="['icon-left', 'ti', `ti-${iconLeft}`, { focus, filled: isFilled }]" @click="emit('left-icon-click')"></i>
            <input
                v-if="type !== 'tel'"
                ref="textInput"
                :id="name"
                :name="name"
                :type="type === 'currency' ? 'text' : type"
                :placeholder="placeholder"
                :disabled="disabled"
                :value="type === 'currency' ? formatCurrency(localValue) : localValue"
                :max="maxDate"
                :min="min"
                :autocomplete="autocomplete ? 'on' : 'off'"
                @input="handleChange"
                @keypress="writingLocker"
                @blur="handleBlur"
                @focus="handleFocus"
            />
            <InputMask
                v-else
                :model-value="modelValue.toString()"
                id="phone"
                mask="99 99 99 99 99"
                fluid
                slotChar=""
                :autoClear="false"
                @update:model-value="handlePhoneChange"
            />
            <span v-if="type === 'currency'" class="currency-suffix">€</span>
            <i v-if="iconRight" :class="['icon-right', 'ti', `ti-${iconRight}`]" @click="emit('right-icon-click')"></i>
            <i v-if="localValid" class="ti ti-check"></i>
            <div v-if="loading" class="loader">
                <Loader />
            </div>
        </div>
        <div v-if="localError" class="input-error">{{ localError }}</div>
    </div>
</template>
<style scoped lang="scss">
@mixin inputShadow($color) {
    box-shadow: 0 0 0 3px $color;
}

$transition-time: 0.2s;

.ds-text-input-component {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    position: relative;
    width: 100%;

    .label-description-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        label {
            @include font-size-14;
            font-weight: 500;
        }

        .description {
            color: $text-sub-600;
            @include font-size-13;
        }
    }

    &.lg {
        label {
            @include font-size-16;
        }
        .description {
            @include font-size-14;
        }
        div.input-error {
            @include font-size-13;
        }
    }

    &.xs {
        label {
            @include font-size-12;
        }
        .description {
            font-size: 14px;
        }
        .input-container {
            padding: 0 10px;
        }
    }

    &.disabled label:not(.enabled) {
        color: $text-disabled-300;
    }

    .required-asterisk {
        color: $blue-base;
    }

    .input-container {
        display: flex;
        height: 36px;
        overflow: hidden;
        width: 100%;
        padding: 0 12px;
        gap: 8px;

        border-radius: 8px;
        border: 1px solid $stroke-soft-200;
        transition: $transition-time;
        .ti {
            font-size: 18px;
            margin: auto 0;
        }

        .ti-check {
            color: $blue-base;
            font-weight: 600;
        }

        .icon-left {
            color: $icon-soft-400;
            transition: $transition-time;
            &.filled {
                color: $icon-strong-900;
            }
            &.focus {
                color: $blue-base;
            }
        }

        .icon-right {
            cursor: pointer;
        }

        &.disabled {
            border-color: $bg-weak-100;
            background-color: $bg-weak-100;
            input {
                color: $text-disabled-300;
                background-color: $bg-weak-100;
            }
            .ti-check {
                color: $stroke-sub-300;
            }
            .icon-right {
                cursor: default;
                pointer-events: none;
            }
        }

        input {
            border: none;
            outline: none;
            padding: 5px 0;
            color: $text-main-900;
            background-color: transparent;
            font-weight: 400;
            width: 100%;
            @include font-size-13;
            &::placeholder {
                @include font-size-13;
                color: $text-soft-400;
                opacity: 1;
            }
        }

        &:hover:not(.disabled, .focus, .error) {
            border-color: $stroke-strong-900;
        }

        &.focus {
            border-color: $blue-base;
        }

        &.lg {
            height: 44px;
            input {
                @include font-size-14;
                &::placeholder {
                    @include font-size-14;
                    color: $text-soft-400;
                    opacity: 1;
                }
            }
        }

        &.md {
            height: 40px;
            .ti {
                font-size: 16px;
            }
        }

        &.sm {
            height: 36px;
            .ti {
                font-size: 16px;
            }
        }

        &.xs {
            height: 28px;
            .ti {
                font-size: 14px;
            }
        }

        &.error {
            border-color: $red-base;
        }

        .currency-suffix {
            align-self: center;
        }
    }

    div.input-error {
        @include font-size-12;
        position: absolute;
        color: $red-base;
        left: 0;
        padding: 0;
        top: 100%;
    }

    .loader {
        display: flex;
        align-items: center;
    }
}
</style>
